import { vars } from '@/app/config/variables';
import { classInstance } from '@/shared/helpers/helpers';

import { SubjectSearch } from '@/features/subject/subject-search/subject-search';
import { SubjectControl } from '@/features/subject/subject-control/subject-control';

export class Subject {
	constructor(selector, options = {}) {
		this.$container = selector;

		if (!this.$container) return;

		// this.selectorGrid = options.selectorGrid || '.js-subject__grid';
		// this.selectorGridSmall = options.selectorGridSmall || '.js-subject__grid-small';
		this.selectorItem = options.selectorItem || '.js-subject__item';
		this.selectorChapter = options.selectorChapter || '.js-subject__chapter';
		this.selectorSearch = options.selectorSearch || '.js-subject-search';
		this.selectorControl = options.selectorControl || '.js-subject-control';
		this.selectorCollapse = options.selectorCollapse || '.js-collapse__element';
		this.selectorCollapseButton = options.selectorCollapseButton || '.js-subject__collapse';

		this.init();
	}

	init() {
		classInstance.set(this.$container, { subject: this });

		this.$search = document.querySelector(this.selectorSearch);
		this.$control = document.querySelector(this.selectorControl);
		this.$gridArr = Array.from(this.$container.querySelectorAll(this.selectorGrid));
		this.$gridSmallArr = Array.from(this.$container.querySelectorAll(this.selectorGridSmall));
		this.$chapterArr = Array.from(this.$container.querySelectorAll(this.selectorChapter));
		this.$collapseArr = Array.from(this.$container.querySelectorAll(this.selectorCollapse));
		this.$collapseButton = this.$container.querySelector(this.selectorCollapseButton);

		this.setResize();
		this.setResizeSmall();

		const $activeChapter = this.$control.querySelector('.js-subject-control__chapter.active');
		this.activeChapter = $activeChapter?.dataset.chapter || 'all';
		this.updateChapter(this.activeChapter);

		const $activeSearch = this.$container.querySelector('.js-subject-search input');
		const $activeLetter = this.$control.querySelector('.js-subject-control__letter.active');
		this.activeLetter = $activeLetter?.dataset.letter || 'all';
		this.activeSearch = $activeSearch?.value || '';
		this.updateLetter(this.activeLetter, this.activeSearch);

		this.subjectSearch = new SubjectSearch(this.$search);
		if (this.$search) {
			this.$search.addEventListener('search', this.searchHandler.bind(this));
		}

		this.subjectControl = new SubjectControl(this.$control);
		this.$control.addEventListener('changeChapter', this.chapterHandler.bind(this));
		this.$control.addEventListener('changeLetter', this.letterHandler.bind(this));

		window.addEventListener('resize', this.resizeHandler.bind(this));
		this.$collapseArr.forEach(($element) => {
			$element.addEventListener('collapseClose', this.collapseHandler.bind(this));
			$element.addEventListener('collapseOpen', this.collapseHandler.bind(this));
		});

		if (this.$collapseButton) {
			this.$collapseButton.addEventListener('click', this.toggleCollapse.bind(this));
		}
	}

	destroy() {
		classInstance.del(this.$container, 'subject');

		this.subjectSearch.destroy();
		this.$search.removeEventListener('search', this.searchHandler);

		this.subjectControl.destroy();
		this.$control.removeEventListener('changeChapter', this.chapterHandler);
		this.$control.removeEventListener('changeLetter', this.letterHandler);

		this.$collapseArr.forEach(($element) => {
			$element.removeEventListener('collapseClose', this.collapseHandler);
			$element.removeEventListener('collapseOpen', this.collapseHandler);
		});
		window.removeEventListener('resize', this.resizeHandler);
		this.$collapseButton.removeEventListener('click', this.toggleCollapse);
		this.setDestroy();
		this.setDestroySmall();
	}

	reinit() {
		this.destroy();
		this.init();
	}

	chapterHandler(event) {
		this.updateChapter(event.detail.value);
	}

	searchHandler(event) {
		this.updateLetter(this.activeLetter, event.detail.value);
	}

	letterHandler(event) {
		this.updateLetter(event.detail.value, this.activeSearch);
	}

	collapseHandler() {
		this.setResize();
		this.setResizeSmall();
	}

	resizeHandler() {
		this.setResize();
		this.setResizeSmall();
	}

	updateChapter(value) {
		const count = this.filteredChapter(this.$chapterArr, value);
		this.$container.classList.toggle('empty', !count);
		this.activeChapter = value;
		this.setResize();
		this.setResizeSmall();
	}

	filteredChapter($items, value) {
		let count = 0;
		$items.forEach(($element) => {
			if (value === $element.dataset.value || value === 'all') {
				$element.classList.add('shown');
				count += 1;
			} else {
				$element.classList.remove('shown');
			}
		});
		return count;
	}

	updateLetter(letter, search) {
		this.$chapterArr.forEach(($element) => {
			const count = this.filteredLetter(
				$element.querySelectorAll(this.selectorItem),
				letter,
				search,
			);
			$element.classList.toggle('empty', !count);
		});
		this.activeLetter = letter;
		this.activeSearch = search;
		this.setResize();
		this.setResizeSmall();
	}

	filteredLetter($items, letter, search) {
		let count = 0;
		$items.forEach(($element) => {
			const matchesLetter = this.isLetter($element, letter);
			const matchesSearch = this.isSearch($element, search);

			if (matchesLetter && matchesSearch) {
				$element.classList.add('shown');
				count += 1;
			} else {
				$element.classList.remove('shown');
			}
		});
		return count;
	}

	isLetter($element, letter) {
		return letter === $element.dataset.value || letter === 'all';
	}

	isSearch($element, search) {
		const $links = Array.from($element.querySelectorAll('.subject-item__link'));
		const $collapseButtons = Array.from($element.querySelectorAll('.subject-item-collapse__button'));
		let hasMatch = false;

		$links.forEach($link => {
			if ($link.textContent.toLowerCase().includes(search.toLowerCase())) {
				$link.classList.add('shown');
				$link.classList.remove('hide');
				hasMatch = true;
			} else {
				$link.classList.remove('shown');
				$link.classList.add('hide');
			}
		});

		$collapseButtons.forEach($btn => {
			if ($btn.textContent.toLowerCase().includes(search.toLowerCase())) {
				$btn.classList.add('shown');
				$btn.classList.remove('hide');
				hasMatch = true;
			} else {
				$btn.classList.remove('shown');
				$btn.classList.add('hide');
			}
		});

		return hasMatch || search === '';
	}

	setActive(column) {
		this.$gridArr.forEach(($grid) => {
			this.resizeGrid($grid, column);
		});
	}

	setActiveSmall(column) {
		this.$gridSmallArr.forEach(($grid) => {
			this.resizeGridSmall($grid, column);
		});
	}

	setDestroy() {
		this.$gridArr.forEach(($grid) => {
			this.destroyGrid($grid);
		});
	}

	setDestroySmall() {
		this.$gridSmallArr.forEach(($grid) => {
			this.destroyGridSmall($grid);
		});
	}

	setResize() {
		if (document.documentElement.clientWidth >= vars.breakpoint.desktopMiddle) {
			this.setActive(3);
			return;
		}
		if (document.documentElement.clientWidth >= vars.breakpoint.desktopSmall) {
			this.setActive(2);
			return;
		}
		this.setDestroy();
	}

	setResizeSmall() {
		if (document.documentElement.clientWidth >= vars.breakpoint.tabletMiddle) {
			this.setActiveSmall(2);
			return;
		}
		if (document.documentElement.clientWidth >= vars.breakpoint.tabletSmall) {
			this.setActiveSmall(1);
			return;
		}
		this.setDestroySmall();
	}

	resizeGrid(grid, column) {
		const $itemArr = Array.from(grid.querySelectorAll(`${this.selectorItem}.shown`));
		const $columnArr = this.chunkify($itemArr, Math.ceil($itemArr.length / column));
		this.calculate(grid, $columnArr);
	}

	resizeGridSmall(grid, column) {
		const $itemArr = Array.from(grid.querySelectorAll(`${this.selectorItem}.shown`));
		const $columnArr = this.chunkify($itemArr, Math.ceil($itemArr.length / column));
		this.calculateSmall(grid, $columnArr);
	}

	destroyGrid($grid) {
		$grid.removeAttribute('style');
		$grid.querySelectorAll(this.selectorItem).forEach(($item) => {
			$item.removeAttribute('style');
		});
	}

	destroyGridSmall($grid) {
		$grid.removeAttribute('style');
		$grid.querySelectorAll(this.selectorItem).forEach(($item) => {
			$item.removeAttribute('style');
		});
	}

	chunkify(array, size) {
		return array.reduce(
			(chunks, item, index) =>
				index % size === 0 ? [...chunks, array.slice(index, index + size)] : chunks,
			[],
		);
	}

	calculate(grid, array) {
		let gridHeight = 0;
		array.forEach((column) => {
			let columnHeight = 70;
			column.forEach(($element) => {
				columnHeight += $element.offsetHeight;
				$element.setAttribute('style', `width:${100 / array.length}%`);
			});
			if (columnHeight > gridHeight) gridHeight = columnHeight;
		});
		grid.setAttribute('style', `height:${gridHeight}px`);
	}

	calculateSmall(grid, array) {
		let gridHeight = 0;
		array.forEach((column) => {
			let columnHeight = 0;
			column.forEach(($element) => {
				columnHeight += $element.offsetHeight;
				$element.setAttribute('style', `width:${100 / array.length}%`);
			});
			if (columnHeight > gridHeight) gridHeight = columnHeight;
		});
		grid.setAttribute('style', `height:${gridHeight}px`);
	}

	toggleCollapse() {
		const hasShown = this.$collapseArr.some($element => $element.classList.contains('shown'));
		const newText = hasShown ? 'Развернуть всё' : 'Свернуть всё';
		this.$collapseButton.querySelector('span').textContent = newText;
		this.$collapseButton.classList.toggle('js-subject__collapse--hide');
		this.$collapseArr.forEach(($element) => {
			$element.classList.toggle('shown', this.$collapseButton.classList.contains('js-subject__collapse--hide'));
		});
	}
}
